import { Graph } from "./Graph";
import * as CONSTANTS from "./constants";
import { ActionLogger } from "./ActionLogger";
import { NodeData } from "./NodeData";

/** storage service class acts as an adapter for the Graph class */
export class Storage {
    /** Graph object holds nodes and edges that have a data member called nodeData and edgeData to allow the graph to be used in multiple applications */
    graph;
    maxShutoffCost;
    maxInfectedCost;
    maxGoodwillCost;
    /** allInfectedNodes keeps track of all nodes that have ever been infected */
    allInfectedNodes = [];
    /** percent nodes caught each round */
    percentNodesCaught = [];

    constructor() {}

    /**
     * Factory function that builds new Storage
     * @return {Storage} instance
     */
    static build() {
        let instance = new Storage();
        instance.graph = Graph.build();
        instance.maxShutoffCost = 0;
        instance.maxInfectedCost = 0;
        instance.maxGoodwillCost = 0;
        return instance;
    }

    /***************** graph builders ***********************/
    /**
     * create nodes from @param nodes and @param data and put them in the graph 
     * @param nodes
     * @param data
     */
    createNodes(nodes, data) {
        this.graph.createNodes(nodes, data);
    }

    /**
     * import edges from @param array edges and put them in the graph
     * @param {Edge[]} edges
     */
    importEdges(edges) {
        this.graph.importEdges(edges);
    }

    /***************** node getters *************************/
    /**
     * get all NodeData objects
     * @return {NodeData[]} 
     */
    getNodeDatas() {
        return this.graph.getNodeDatas();
    }

    /**
     * get all node datas prepped for client
     * @param {string} role
     * @param {bool} gameInProgress
     * @return array of nodes
     */
    getNodeDatasForClient(role, gameInProgress) {
        let nodes = {};
        let nodeDatas = this.getNodeDatas();
        for (let nodeName of Object.keys(nodeDatas)) {
            let node = NodeData.jsonDeserialize(JSON.parse(JSON.stringify(nodeDatas[nodeName])));
            node.prepForClient(role, gameInProgress);
            nodes[nodeName] = node;
        }
        return nodes;
    }

    /**
     * get a specific NodeData based upon @param string nodeName
     * @param {string} nodeName
     * @return {NodeData} data
     */
    getNodeData(nodeName) {
        return this.graph.getNodeData(nodeName);
    }

    /**
     * Return a random Node Name that is not included in @param nodes
     * @param {string[]} nodes = []
     * @return {string} nodeName 
     */
    getRandomNodeName(nodes = []) { // TODO: this doesn't seem to work properly
        let node = this.getNodeNames()[Math.floor(Math.random() * (this.graph.getNodeCount() - 1))];
        if (nodes.includes(node)) {
            return this.getRandomNodeName();
        }
        return node;
    }

    /**
     * get all node Names
     */
    getNodeNames() {
        return this.graph.getNodeKeys();
    }

    /** returns the names of all nodes with a certain status 
     * COMPROMISED will return all kinds of infected nodes
     * @param {string} statusType
     * @return matching Nodes
     * TODO make function accept a function as a parameter and pass that into filter
    */
    getNodesByStatus(statusType) {
        if (statusType === CONSTANTS.COMPROMISED) {
            let nodeDatas = this.getNodeDatas();
            return Object.keys(nodeDatas).filter(nodeName => {
                return nodeDatas[nodeName].isInfected();
            });
        }
        else {
            let returnNodes = [];
            for (let node of Object.values(this.getNodeDatas())) {
                if (node.getStatus() === statusType) {
                    returnNodes.push(node.getName());
                }
            }
            return returnNodes;
        }
    }

    /***************** node updaters *************************/
    
    /** appends new notifications to a single node and returns all notifications 
     * @param {string} nodeName
     * @param newNotifications
     * @return array of nodes notifications
     */
    updateNodeNotifications(nodeName, newNotifications) {
        let node = this.getNodeData(nodeName);
        for (let role of CONSTANTS.ALL_ROLES) {
            node.addNotifications(newNotifications, role);
        }
        return node.getNotifications();
    }

    /***************** edge getters *************************/
    /**
     * get all edges
     * @return {Edge[]} array of edges
     */
    getEdges() {
        return this.graph.getEdges();
    }

    /**
     * get a particular edge's status
     * @param {string} source
     * @param {string} destination
     * @return {string | null} status
     */
    getEdgeStatus(source, destination) {
        let edge = this.graph.getEdgeRawData(source, destination);
        return edge === null ? null : edge.status;
    }

    /**
     * set a certain edge's status
     * @param {string} source
     * @param {string} destination
     * @param {string} status
     */
    setEdgeStatus(source, destination, status) {
        this.getEdge(source, destination).getData().status = status;
    }

    /**
     * get Edge Data from particular edge
     * @param {string} source
     * @param {string} destination
     * @return {Edge} Data
     */
    getEdge(source, destination) {
        return this.graph.getEdge(source, destination);
    }

    /** get a list of connections between a certain set of nodes 
     * @param {Node[]} nodes
     * @return array
     */
    getVisibleConnections(nodes) {
        let nodeNames = []
        for (let node of Object.values(nodes)) {
            nodeNames.push(node.getName());
        }
        let visibleConnections = this.graph.getComponentEdgeDatas(nodeNames);
        return visibleConnections;
    }

    /**
     * set a certain edges status to DOWN
     * @param {string} source
     * @param {string} destination
     */
    disconnectEdge(source, destination) {
        this.setEdgeStatus(source, destination, CONSTANTS.DOWN);
    }

    /**
     * get info for client. Usually used for the getInfo api
     * @param {Session} session
     * @return {Object} info
     */
    getAdditionalInfo(session) {
        let roles = {};
        for (let key of Object.keys(session.getRoles())) {
            roles[key] = {
                "actionsLeft": session.gameInProgress() ? session.getRoles()[key].actionsLeftThisRound : 0,
                "ready": session.getRoles()[key].ready,
                "usernames": session.getRoles()[key].usernames
            }
        }
        let info = {
            "round": session.getRound(),
            "totalRounds": session.getConfig().getTotalRounds(),
            "isInBreak": session.isInBreak(),
            "lossIncurred": session.calculateLossIncurred(),
            "roundStartTimeStamp": session.getRoundStartTimeStamp(),
            "roles": roles,
            "percentNodesCaughtSimple": this.calculatePercentOfNodesCaught(session.getNumNodesCaught()),
            "started": session.isStarted(),
            "drill": session.getConfig().isDrill(),
        }
        if (!session.gameInProgress()) {
            info["finalResults"] = session.getFinalReport();
        }
        return info;
    }

    savePercentNodesCaught(numNodesCaught, round) {
        this.percentNodesCaught[round] = this.calculatePercentOfNodesCaught(numNodesCaught);
    }

    /** nodesCaught / the number of nodes that were ever infected
     * @param  {number} numNodesCaught
     * @return {number} percent 
     */
    calculatePercentOfNodesCaught(numNodesCaught) {
        return (numNodesCaught / this.allInfectedNodes.length) * 100;
    }

    /** get NodeData objects from neighbors if edge is active
     * @param {string} nodeName
     * @return {NodeData[]} neighbors
     */
    getNodeNeighborDatas(nodeName) {
        let neighbors = [];
        for (let nodeData of this.graph.getNodeNeighborDatas(nodeName)) {
            let edge = this.getEdgeStatus(nodeName, nodeData.getName());
            let logger = ActionLogger.getLogger();
            logger.log(edge);
            if (edge === CONSTANTS.UP) {
                neighbors.push(nodeData);
            }
        }
        return neighbors;
    }

    /** getNodeEdges; return an array of all edges connected to a given node
     * @param {string} nodeName
     * @return {Edge[]} edges
     */
    getNodeEdges(nodeName) {
        let neighbors = [];
        for (let edge of this.graph.getNodeEdges(nodeName)) {
            if (edge.getData().status === CONSTANTS.UP) {
                neighbors.push(edge);
            }
        }
        return neighbors;
    }

    toJSON() {
        return {
            graph: this.graph,
            max_shutoff_cost: this.maxShutoffCost,
            max_infected_cost: this.maxInfectedCost,
            max_goodwill_cost: this.maxGoodwillCost,
            all_infected_nodes: this.allInfectedNodes,
            percent_nodes_caught: this.percentNodesCaught
        };
    }

    static jsonDeserialize(jsonString) {
        let instance = new Storage();
        let data = jsonString;
        instance.graph = Graph.jsonDeserialize(data.graph);
        instance.maxShutoffCost = data.max_shutoff_cost;
        instance.maxInfectedCost = data.max_infected_cost;
        instance.maxGoodwillCost = data.max_goodwill_cost;
        instance.allInfectedNodes = data.all_infected_nodes;
        instance.percentNodesCaught = data.percent_nodes_caught;
        return instance;
    }

    /**
     * add a role to the attack visibility of a node so that that role will also see the node as suspicious 
     * @param {string} nodeName
     * @param {string} newAsvRole
     * @return attack visibility
     */
    appendRoleToAttackVisibility(nodeName, newAsvRole) {
        let node = this.getNodeData(nodeName);
        node.pushAttackVisibility(newAsvRole);
        return node.getAttackVisibility();
    }

    /**
     * @param {Object} visibleTypes
     * @param {string} role
     * @param {bool} gameInProgress
     * @return {Node[]} nodes 
     */
    getVisibleNodesForClient(visibleTypes, role, gameInProgress) {
        let visibleNodes = [];
        for (let node of Object.values(this.getNodeDatasForClient(role, gameInProgress))) {
            if (visibleTypes.includes(node.getType()) || visibleTypes.includes(node.getGroup())) {
                visibleNodes.push(node);
            }
        }
        return visibleNodes;
    }

    /** get the view for the scada role
     * @param {bool} gameInProgress
     * @return {Object} nodes and edges
     */
    getViewScada(gameInProgress) {
        let scadaVisibleGroups = [CONSTANTS.HOSPITAL_GROUP, CONSTANTS.WATER_GROUP, CONSTANTS.ELECTRIC_GROUP, CONSTANTS.TRANSPORTATION_GROUP];
        let visibleNodes = this.getVisibleNodesForClient(scadaVisibleGroups, CONSTANTS.SCADA, gameInProgress);
        return this.nodesAndConnections(visibleNodes, CONSTANTS.SCADA);
    }

    /**
     * get the view for the IT role
     * @param {bool} gameInProgress
     * @return {Object} nodes and edges
     */
    getViewIt(gameInProgress) {
        let ItVisibleDeviceTypes = [CONSTANTS.PC, CONSTANTS.SERVER];
        let visibleNodes = this.getVisibleNodesForClient(ItVisibleDeviceTypes, CONSTANTS.IT, gameInProgress);
        return this.nodesAndConnections(visibleNodes, CONSTANTS.IT);
    }

    /**
     * get the view for the SOC role
     * @param {bool} gameInProgress
     * @return {Object} nodes and edges
     */
    getViewSoc(gameInProgress) {
        return this.nodesAndConnections(this.getNodeDatasForClient(CONSTANTS.SOC, gameInProgress), CONSTANTS.SOC);
    }

    /**
     * get view for the PIO role
     * @param {bool} gameInProgress
     * @return {Object} nodes and edges
     */
    getViewPio(gameInProgress) {
        return this.nodesAndConnections(this.getNodeDatasForClient(CONSTANTS.PIO, gameInProgress), CONSTANTS.PIO);
    }

    /**
     * prep nodes and edges to be passed to view getters
     * @param {Node[]} nodes
     * @param {string} role
     * @return {Object} nodes and edges
     */
    nodesAndConnections(nodes, role) {
        let nodesAndEdges = {};
        nodesAndEdges["nodes"] = nodes;
        nodesAndEdges["edges"] = role !== CONSTANTS.PIO ? this.getVisibleConnections(nodes) : [];
        return nodesAndEdges;
    }

    /** updates the status of a single node, returning that node's attributes
     * Update the status of a node and update this.allInfectedNodes if a node is infected
     * @param {string} nodeName
     * @param attackVisibleRoles
     * @param {string} newStatus
     * @param {number} round
     * @return {NodeData} node
     * @throws Exception if something tries to infect an already infected node as that behavior is not supposed to happen
     */
    updateNodeStatus(nodeName, attackVisibleRoles, newStatus, round) {
        if (newStatus === CONSTANTS.INFECTED_SPREAD || newStatus === CONSTANTS.INFECTED_DIRECTED) {
            if (this.getNodeData(nodeName).isInfected()) {
                throw new Error("Cannot reinfect an already infected node");
            }
            this.allInfectedNodes.push(nodeName);
        }
        this.getNodeData(nodeName).updateStatus(attackVisibleRoles, newStatus, round);
        let node = this.getNodeData(nodeName);
        return node;
    }

    /** updates the status of an arbitrary number of nodes, chosen by name 
     * @param {string[]} nodeNames
     * @param attackVisibleRoles
     * @param {string} newStatus
     * @param {number} round
     * @return {Node[]} nodes
    */
    updateNodeStatuses(nodeNames, attackVisibleRoles, newStatus, round) {
        let nodes = [];
        for (let name of nodeNames) {
            nodes.push(this.updateNodeStatus(name, attackVisibleRoles, newStatus, round));
        }
        return nodes;
    }

    /**
     * investiage a node
     * @param {string} nodeName
     * @return {NodeData} node
     */
    investigateNode(nodeName) {
        let node = this.getNodeData(nodeName);
        node.investigate();
        return node;
    }

    getAllInfectedNodes() {
        return this.allInfectedNodes;
    }

    getMaxShutoffCost() {
        return this.maxShutoffCost;
    }

    calculateCosts() {
        this.maxShutoffCost = Math.max(...Object.values(this.getNodeDatas()).map(node => node.getCostShutoff()));
        this.maxInfectedCost = Math.max(...Object.values(this.getNodeDatas()).map(node => node.getCostInfected()));
        this.maxGoodwillCost = Math.max(...Object.values(this.getNodeDatas()).map(node => node.getCostGoodwill()));  
    }
}